<template>
    <div class="body d-flex py-lg-3 py-md-2 mb-5" v-if="hasProjectManagement">
        <div class="container-xxl">

            <div class="d-flex justify-content-end align-items-center mb-4 flex-wrap gap-2 no-print">
                <!-- <h4 class="fw-bold mb-0">Reporting &amp; Analytics</h4> -->
                <div class="d-flex align-items-center gap-2">
                    <div class="dropdown" @click="toggleExportMenu" @blur="closeExportMenu" tabindex="0">
                        <button type="button" class="btn btn-dark test" title="Εξαγωγή αναφοράς">
                            <i class="icofont-download"></i>
                        </button>
                        <ul class="dropdown-menu reportingAnalytics border-0 shadow p-2" :class="{ show: isExportMenuOpen }">
                            <li><a class="dropdown-item rounded" href="#" @click.prevent="exportToPdf">Εξαγωγή σε PDF</a></li>
                            <li><a class="dropdown-item rounded" href="#" @click.prevent="exportToExcel">Εξαγωγή σε Excel</a></li>
                        </ul>
                    </div>
                    <button type="button" class="btn btn-dark test" @click="fetchKpiData" :disabled="isLoading" title="Ανανέωση">
                        <i class="icofont-refresh"></i>
                    </button>
                    
                </div>
            </div>

            <!-- Summary KPI tiles -->
            <div class="row g-3 mb-4">
                <div class="col-6 col-md-3">
                    <div class="card border-0 kpi-tile">
                        <div class="card-body">
                            <div class="kpi-label">Ενεργά Projects</div>
                            <div class="kpi-value">{{ projectsKpi.length }}</div>
                        </div>
                    </div>
                </div>
                <div class="col-6 col-md-3">
                    <div class="card border-0 kpi-tile">
                        <div class="card-body">
                            <div class="kpi-label">Ώρες Εργασίας</div>
                            <div class="kpi-value">{{ formatHours(totalHoursLogged) }}</div>
                        </div>
                    </div>
                </div>
                <div class="col-6 col-md-3">
                    <div class="card border-0 kpi-tile">
                        <div class="card-body">
                            <div class="kpi-label">Εκτιμώμενες Ώρες</div>
                            <div class="kpi-value">{{ formatHours(totalEstimatedHours) }}</div>
                        </div>
                    </div>
                </div>
                <div class="col-6 col-md-3">
                    <div class="card border-0 kpi-tile">
                        <div class="card-body">
                            <div class="kpi-label">Μέση Αξιοποίηση</div>
                            <div class="kpi-value" :style="{ color: statusColor(averageUtilization) }">
                                {{ Math.round(averageUtilization) }}%
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="isLoading" class="text-center py-5">
                <p>Φόρτωση δεδομένων...</p>
            </div>

            <div v-else-if="!projectsKpi.length" class="text-center py-5">
                <p>Δεν υπάρχουν ενεργά projects με καταχωρημένες εκτιμώμενες ώρες για ανάλυση.</p>
            </div>

            <template v-else>
                <!-- Grouped stacked bar chart: ομάδες = Projects/Tasks/Subtasks ανά μήνα (με βάση το deadline τους),
                     στοίβα ανά ομάδα = θετικό τμήμα εντός προθεσμίας / αρνητικό τμήμα εκτός χρονικής προθεσμίας -->
                <div class="card border-0 mb-4">
                    <div class="card-body">
                        <div class="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-1">
                            <h6 class="fw-bold mb-1">Απόδοση Projects / Tasks / Subtasks ανά Χρονική Προθεσμία</h6>
                            <div class="btn-group no-print" role="group">
                                <button type="button" class="btn btn-sm"
                                    :class="deadlineChartMetric === 'hours' ? 'btn-dark test' : 'btn-outline-dark'"
                                    @click="deadlineChartMetric = 'hours'">Ώρες</button>
                                <button type="button" class="btn btn-sm"
                                    :class="deadlineChartMetric === 'count' ? 'btn-dark test' : 'btn-outline-dark test'"
                                    @click="deadlineChartMetric = 'count'">Πλήθος</button>
                            </div>
                        </div>
                        
                        <div class="d-flex align-items-center gap-2 mb-2 small-14 no-print" v-if="selectedUserId">
                            <span class="badge bg-dark">Φίλτρο χρήστη: {{ selectedUserName }}</span>
                            <a href="#" class="text-secondary" @click.prevent="selectedUserId = null">Καθαρισμός φίλτρου</a>
                        </div>
                        <div id="deadline_performance_chart"></div>
                        <div class="d-flex flex-wrap gap-3 mt-2 small-14 text-secondary align-items-start">
                            <span>▲ Πάνω από το 0 = εντός προθεσμίας (πράσινο) &nbsp;|&nbsp; ▼ Κάτω από το 0 = εκτός χρονικής προθεσμίας (κόκκινο). Κάθε χρήστης έχει τη δική του απόχρωση.</span>
                        </div>
                        <div class="d-flex flex-wrap gap-3 mt-1 small-14 text-secondary" v-if="!selectedUserId && deadlineLegend.length">
                            <span v-for="entry in deadlineLegend" :key="entry.key">
                                <span class="legend-dot" :style="{ background: entry.onTimeColor }"></span><span
                                    class="legend-dot" :style="{ background: entry.overdueColor }"></span>{{ entry.label }}
                            </span>
                        </div>
                    </div>
                </div>

                <!-- Comparative performance reports -->
                <div class="row g-3">
                    <div class="col-lg-6">
                        <div class="card border-0 h-100">
                            <div class="card-body">
                                <h6 class="fw-bold mb-3">Συγκριτική Απόδοση Προσωπικού</h6>
                                <ul class="nav nav-tabs mb-3 no-print" role="tablist">
                                    <li class="nav-item">
                                        <a class="nav-link" :class="{ active: personnelViewTab === 'projects' }" href="#"
                                            @click.prevent="personnelViewTab = 'projects'">Projects</a>
                                    </li>
                                    <li class="nav-item">
                                        <a class="nav-link" :class="{ active: personnelViewTab === 'tasks' }" href="#"
                                            @click.prevent="personnelViewTab = 'tasks'">Tasks</a>
                                    </li>
                                </ul>
                                <div class="table-responsive" v-if="personnelViewTab === 'projects'">
                                    <table class="table table-sm align-middle mb-0 mis_table">
                                        <thead>
                                            <tr>
                                                <th>Χρήστης</th>
                                                <th>Ώρες / Εκτίμηση</th>
                                                <th>%</th>
                                                <th style="width:30%"></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="person in personnelPerformance" :key="person.userId"
                                                :class="{ 'selected-user-row': selectedUserId === person.userId }">
                                                <td>{{ person.username }}</td>
                                                <td>{{ formatHours(person.hoursLogged) }} / {{ formatHours(person.estimateShare) }}</td>
                                                <td :style="{ color: statusColor(person.percentage) }">{{ Math.round(person.percentage) }}%</td>
                                                <td class="d-flex align-items-center gap-2">
                                                    <div class="bar-track flex-grow-1">
                                                        <div class="bar-fill" :style="{ width: Math.min(person.percentage, 100) + '%', background: statusColor(person.percentage) }"></div>
                                                    </div>
                                                    <button type="button" class="btn btn-sm p-0 no-print"
                                                        :class="selectedUserId === person.userId ? 'text-danger' : 'text-secondary'"
                                                        :title="selectedUserId === person.userId ? 'Καθαρισμός φίλτρου' : 'Προβολή στο διάγραμμα προθεσμιών'"
                                                        @click="toggleUserFilter(person.userId)">
                                                        <i :class="selectedUserId === person.userId ? 'icofont-close-circled' : 'icofont-filter'"></i>
                                                    </button>
                                                </td>
                                            </tr>
                                            <tr v-if="!personnelPerformance.length">
                                                <td colspan="4">Δεν υπάρχουν δεδομένα προσωπικού.</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                                <div class="table-responsive" v-else>
                                    <table class="table table-sm align-middle mb-0 mis_table">
                                        <thead>
                                            <tr>
                                                <th>Χρήστης / Task</th>
                                                <th>Ώρες</th>
                                                <th style="width:30%"></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <template v-for="person in personnelPerformance" :key="person.userId">
                                                <tr class="clickable-row" style="cursor:pointer"
                                                    :class="{ 'selected-user-row': selectedUserId === person.userId }"
                                                    @click="person.show = !person.show">
                                                    <td>{{ person.username }}</td>
                                                    <td>{{ formatHours(person.hoursLogged) }}</td>
                                                    <td>
                                                        <button type="button" class="btn btn-sm p-0 no-print"
                                                            :class="selectedUserId === person.userId ? 'text-danger' : 'text-secondary'"
                                                            :title="selectedUserId === person.userId ? 'Καθαρισμός φίλτρου' : 'Προβολή στο διάγραμμα προθεσμιών'"
                                                            @click.stop="toggleUserFilter(person.userId)">
                                                            <i :class="selectedUserId === person.userId ? 'icofont-close-circled' : 'icofont-filter'"></i>
                                                        </button>
                                                    </td>
                                                </tr>
                                                <tr v-if="person.show" v-for="task in person.tasks" :key="task.taskId"
                                                    class="reports_tasks" style="font-weight: lighter;">
                                                    <td>
                                                        <router-link
                                                            :to="{ path: 'project', query: { id: task.projectId, activeTab: 'report' } }">
                                                            {{ task.projectCode }}
                                                        </router-link>
                                                        — {{ task.title }}
                                                    </td>
                                                    <td>{{ formatHours(task.hours) }}</td>
                                                    <td></td>
                                                </tr>
                                            </template>
                                            <tr v-if="!personnelPerformance.length">
                                                <td colspan="3">Δεν υπάρχουν δεδομένα προσωπικού.</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="col-lg-6">
                        <div class="card border-0 h-100">
                            <div class="card-body">
                                <h6 class="fw-bold mb-3">Συγκριτική Απόδοση Ομάδων (Projects)</h6>
                                <div class="table-responsive">
                                    <table class="table table-sm align-middle mb-0 mis_table">
                                        <thead>
                                            <tr>
                                                <th>Project</th>
                                                <th>Ώρες / Εκτίμηση</th>
                                                <th>%</th>
                                                <th style="width:30%"></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="project in projectsKpi" :key="project.id">
                                                <td>
                                                    <router-link :to="{ path: 'project', query: { id: project.id, activeTab: 'report' } }">
                                                        {{ project.code }}
                                                    </router-link>
                                                </td>
                                                <td>{{ formatHours(project.hoursLogged) }} / {{ formatHours(project.estimatedTime) }}</td>
                                                <td :style="{ color: statusColor(project.percentage) }">{{ Math.round(project.percentage) }}%</td>
                                                <td>
                                                    <div class="bar-track">
                                                        <div class="bar-fill" :style="{ width: Math.min(project.percentage, 100) + '%', background: statusColor(project.percentage) }"></div>
                                                    </div>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </template>

        </div>
    </div>
</template>

<script>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import ApexCharts from 'apexcharts';
import { mapGetters } from 'vuex';
import { fetchAll, getReportData } from '@/services/projects';
import { fetchAll as fetchAllTasks } from '@/services/tasks';
import { fetchAll as fetchAllSubtasks } from '@/services/subtasks';

export default {
    name: 'ReportingAnalyticsMenu',
    computed: {
        ...mapGetters({
            getSuperUserPermission: 'getSuperUserPermission',
            getUserPackets: 'getUserPackets'
        }),
        superUser() {
            return this.getSuperUserPermission === 1;
        },
        hasProjectManagement() {
            return this.getUserPackets.includes('PROJECT MANAGEMENT');
        }
    },
    setup() {
        const isLoading = ref(true);
        const projectsKpi = ref([]);
        const personnelPerformance = ref([]);
        const personnelViewTab = ref('projects');
        const isExportMenuOpen = ref(false);
        const chartInstances = {};

        // Ανά μήνα (τελευταίοι MONTHS_WINDOW μήνες) grouped stacked bar chart: Projects / Tasks / Subtasks
        // ταξινομημένα με βάση το deadline (endDate) τους, εντός/εκτός χρονικής προθεσμίας.
        const MONTHS_WINDOW = 6;
        const STATUS_DONE_ID = 3; // (1=On hold,2=On progress,3=Done,4=Archived,5=On going)
        const GREEK_MONTHS = ['Ιαν', 'Φεβ', 'Μαρ', 'Απρ', 'Μάι', 'Ιουν', 'Ιουλ', 'Αυγ', 'Σεπ', 'Οκτ', 'Νοε', 'Δεκ'];
        const deadlineChartMetric = ref('hours'); // 'hours' | 'count'
        const selectedUserId = ref(null); // null = όλοι οι χρήστες (stacking ανά χρήστη), αλλιώς φιλτράρει σε έναν
        const deadlineDataset = { projects: [], tasks: [], subtasks: [] };
        // Ένα οκτάβημα (8 βήματα) πράσινης απόχρωσης για εντός προθεσμίας και το αντίστοιχο κόκκινης για εκτός 
        const GREEN_SHADES = ['#279d3f', '#52af63', '#279d3f', '#2cae46', '#3e7549', '#2c6338', '#195128', '#014018'];
        const RED_SHADES = ['#da2e2e', '#c95143', '#ce4747', '#da2e2e', '#a5443c', '#90302a', '#7a1c18', '#650105'];
        const OTHER_GREEN = '#9fb79f'; // "Λοιποί" εντός προθεσμίας — χρήστες πέρα από τους 8 πρώτους σε ώρες
        const OTHER_RED = '#c99a94'; // "Λοιποί" εκτός προθεσμίας
        const UNASSIGNED_GREEN = '#c7d6c7'; // στοιχεία χωρίς κανέναν contributor, εντός προθεσμίας
        const UNASSIGNED_RED = '#e0c4c1'; // στοιχεία χωρίς κανέναν contributor, εκτός προθεσμίας
        let userRankMap = {}; // userId -> index 0..7 (ίδιο βήμα και στις δύο ράμπες), υπολογίζεται στο fetchKpiData
        const deadlineLegend = ref([]); // [{ key, label, onTimeColor, overdueColor }] για το custom legend κάτω από το chart

        const parseHoursValue = (value) => {
            if (value === null || value === undefined || value === '') return 0;
            if (typeof value === 'number') return value;
            const strValue = String(value).trim();
            if (strValue.includes(':')) {
                const [hours, minutes] = strValue.split(':').map(Number);
                return (hours || 0) + (minutes || 0) / 60;
            }
            const parsed = parseFloat(strValue.replace(',', '.'));
            return isNaN(parsed) ? 0 : parsed;
        };

        const formatHours = (value) => `${Math.round(value * 10) / 10}h`;

        const statusColor = (percentage) => {
            if (percentage >= 100) return '#E4364D'; // Υπέρβαση εκτίμησης
            if (percentage >= 80) return '#ECC308'; // Κοντά στο όριο
            return '#42bb0a'; // Εντός εκτίμησης
        };

        const toggleExportMenu = () => {
            isExportMenuOpen.value = !isExportMenuOpen.value;
        };

        const closeExportMenu = () => {
            isExportMenuOpen.value = false;
        };

        const destroyCharts = () => {
            Object.values(chartInstances).forEach((chart) => chart.destroy());
            Object.keys(chartInstances).forEach((key) => delete chartInstances[key]);
        };

        // Δέχεται ημερομηνίες σε μορφή "YYYY-MM-DD[ HH:mm:ss]" ή "DD/MM/YYYY" (οι δύο μορφές που χρησιμοποιεί το app)
        const parseDateValue = (value) => {
            if (!value) return null;
            const str = String(value).trim();
            if (!str) return null;

            if (/^\d{4}-\d{2}-\d{2}/.test(str)) {
                const date = new Date(str.replace(' ', 'T'));
                return isNaN(date) ? null : date;
            }

            const slashParts = str.split('/');
            if (slashParts.length === 3) {
                const [day, month, year] = slashParts.map(Number);
                const date = new Date(year, month - 1, day);
                return isNaN(date) ? null : date;
            }

            const date = new Date(str);
            return isNaN(date) ? null : date;
        };

        const monthKey = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

        const buildMonthBuckets = (count) => {
            const now = new Date();
            const months = [];
            for (let i = count - 1; i >= 0; i--) {
                const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
                months.push({ key: monthKey(date), label: `${GREEK_MONTHS[date.getMonth()]} ${date.getFullYear()}` });
            }
            return months;
        };

        // Ταξινομεί ένα project/task/subtask στον μήνα λήξης του, χαρακτηρίζοντάς το εκτός χρονικής προθεσμίας
        // όταν η ημερομηνία λήξης έχει περάσει και δεν έχει ολοκληρωθεί (status !== Done). Το "contributors" κρατάει
        // τις ώρες ανά χρήστη (userId -> ώρες) ώστε το chart να μπορεί να φιλτράρεται ανά χρήστη.
        const classifyDeadlineEntity = (list, { endDate, statusId, hours, label, contributors }) => {
            const date = parseDateValue(endDate);
            if (!date) return; // χωρίς ημερομηνία λήξης δεν μπορεί να τοποθετηθεί ανά μήνα
            const overdue = statusId !== STATUS_DONE_ID && date < new Date();
            list.push({ monthKey: monthKey(date), overdue, hours: hours || 0, label: label || '-', contributors: contributors || {} });
        };

        // userIds === null  -> όλα τα στοιχεία μετρούν πλήρως (χρησιμοποιείται μόνο σε ήδη προ-φιλτραρισμένες λίστες)
        // userIds === [...] -> μετράει μόνο το μερίδιο ωρών των συγκεκριμένων χρηστών ανά στοιχείο
        const aggregateDeadlineSeries = (list, months, metric, userIds) => {
            const onTime = months.map(() => 0);
            const overdue = months.map(() => 0);
            const idSet = userIds ? new Set(userIds) : null;

            list.forEach((item) => {
                const idx = months.findIndex((m) => m.key === item.monthKey);
                if (idx === -1) return; // εκτός του παραθύρου των τελευταίων μηνών

                let value;
                if (!idSet) {
                    value = metric === 'count' ? 1 : item.hours;
                } else {
                    const relevant = Object.keys(item.contributors).filter((uid) => idSet.has(uid));
                    if (!relevant.length) return; // κανένας από τους ζητούμενους χρήστες δεν συνεισέφερε εδώ
                    value = metric === 'count' ? 1 : relevant.reduce((sum, uid) => sum + (item.contributors[uid] || 0), 0);
                }

                if (item.overdue) overdue[idx] += value;
                else onTime[idx] += value;
            });

            return {
                onTime: onTime.map((v) => Math.round(v * 10) / 10),
                overdue: overdue.map((v) => -Math.round(v * 10) / 10) // αρνητικές τιμές = εκτός χρονικής προθεσμίας
            };
        };

        // Λεπτομέρειες tooltip: ποια projects/tasks/subtasks απαρτίζουν το τμήμα που κάνει hover ο χρήστης.
        // userIdsSpec: '__unassigned__' (στοιχεία χωρίς κανέναν contributor) ή πίνακας userIds ή null (όλα)
        const buildDeadlineTooltipHtml = (list, month, isOverdue, metric, userIdsSpec) => {
            const items = list.filter((item) => {
                if (item.monthKey !== month.key || item.overdue !== isOverdue) return false;
                const contributorIds = Object.keys(item.contributors);
                if (userIdsSpec === '__unassigned__') return contributorIds.length === 0;
                if (Array.isArray(userIdsSpec)) return contributorIds.some((uid) => userIdsSpec.includes(uid));
                return true;
            });

            if (!items.length) {
                return `<div class="p-2 small-14 text-secondary">Καμία εγγραφή</div>`;
            }

            const rows = items.slice(0, 20).map((item) => {
                const value = Array.isArray(userIdsSpec)
                    ? Object.keys(item.contributors).filter((uid) => userIdsSpec.includes(uid))
                        .reduce((sum, uid) => sum + (item.contributors[uid] || 0), 0)
                    : item.hours;
                const suffix = metric === 'count' ? '' : ` — ${formatHours(value)}`;
                return `<div>${escapeHtml(item.label)}${suffix}</div>`;
            }).join('');
            const more = items.length > 20 ? `<div class="text-secondary">+ ${items.length - 20} ακόμα</div>` : '';

            return `<div class="apexcharts-tooltip-title p-2" style="max-width:320px; white-space:normal;">${rows}${more}</div>`;
        };

        // Grouped stacked bar chart: 3 ομάδες (Projects/Tasks/Subtasks) ανά μήνα. Μέσα σε κάθε ομάδα, η στοίβα
        // πάνω από το μηδέν = εντός προθεσμίας, κάτω από το μηδέν = εκτός χρονικής προθεσμίας (ίδια λογική
        // "αρνητικής τοποθέτησης" με πριν), και κάθε στοίβα χωρίζεται περαιτέρω ανά χρήστη (μέχρι 8 με ξεχωριστό
        // χρώμα, οι υπόλοιποι μαζεύονται στο "Λοιποί"), ώστε να διαφοροποιούνται οι συνεισφορές τους.
        const buildDeadlineChartOptions = () => {
            const months = buildMonthBuckets(MONTHS_WINDOW);
            const categories = months.map((m) => m.label);
            const metric = deadlineChartMetric.value;
            const filterUserId = selectedUserId.value;

            const groups = [
                { key: 'projects', name: 'Projects', data: deadlineDataset.projects },
                { key: 'tasks', name: 'Tasks', data: deadlineDataset.tasks },
                { key: 'subtasks', name: 'Subtasks', data: deadlineDataset.subtasks }
            ];

            const series = [];
            // onTimeColor/overdueColor: το ίδιο "βήμα" (rank) απόχρωσης στην πράσινη/κόκκινη ράμπα αντίστοιχα,
            // ώστε το βήμα να ταυτοποιεί τον χρήστη είτε είναι εντός είτε εκτός προθεσμίας.
            const pushPair = (group, label, onTimeColor, overdueColor, dataList, dataUserIds, tooltipUserIds) => {
                const { onTime, overdue } = aggregateDeadlineSeries(dataList, months, metric, dataUserIds);
                series.push({
                    name: `${group.name} — ${label} (Εντός)`, group: group.key, data: onTime, color: onTimeColor,
                    meta: { groupKey: group.key, isOverdue: false, userIds: tooltipUserIds }
                });
                series.push({
                    name: `${group.name} — ${label} (Εκτός)`, group: group.key, data: overdue, color: overdueColor,
                    meta: { groupKey: group.key, isOverdue: true, userIds: tooltipUserIds }
                });
            };

            groups.forEach((group) => {
                if (filterUserId) {
                    const username = personnelPerformance.value.find((p) => p.userId === filterUserId)?.username || filterUserId;
                    const rank = userRankMap[filterUserId];
                    const onTimeColor = rank !== undefined ? GREEN_SHADES[rank] : OTHER_GREEN;
                    const overdueColor = rank !== undefined ? RED_SHADES[rank] : OTHER_RED;
                    pushPair(group, username, onTimeColor, overdueColor, group.data, [filterUserId], [filterUserId]);
                    return;
                }

                const topUserIds = Object.keys(userRankMap)
                    .filter((uid) => group.data.some((item) => uid in item.contributors));
                topUserIds.forEach((uid) => {
                    const username = personnelPerformance.value.find((p) => p.userId === uid)?.username || uid;
                    const rank = userRankMap[uid];
                    pushPair(group, username, GREEN_SHADES[rank], RED_SHADES[rank], group.data, [uid], [uid]);
                });

                const otherUserIds = new Set();
                group.data.forEach((item) => {
                    Object.keys(item.contributors).forEach((uid) => { if (!(uid in userRankMap)) otherUserIds.add(uid); });
                });
                if (otherUserIds.size) {
                    const ids = [...otherUserIds];
                    pushPair(group, 'Λοιποί', OTHER_GREEN, OTHER_RED, group.data, ids, ids);
                }

                const unassignedItems = group.data.filter((item) => !Object.keys(item.contributors).length);
                if (unassignedItems.length) {
                    pushPair(group, 'Χωρίς ανάθεση', UNASSIGNED_GREEN, UNASSIGNED_RED, unassignedItems, null, '__unassigned__');
                }
            });

            const values = series.flatMap(s => s.data)

            const maxAbs = Math.max(
                ...values.map(v => Math.abs(v)),
                1
            )

            const padding = maxAbs * 0.1

            return {
                chart: { height: 380, type: 'bar',background: '#dbd1d6', stacked: true, toolbar: { show: false } },
                plotOptions: { bar: { columnWidth: '75%' } },
                dataLabels: { enabled: false },
                legend: { show: false },
                series,
                xaxis: { categories },
                yaxis: {
                        min: -(maxAbs + padding),
                        max: maxAbs + padding,
                    title: { text: metric === 'count' ? 'Πλήθος στοιχείων' : 'Ώρες' },
                    labels: { formatter: (val) => Math.abs(Math.round(val * 10) / 10) }
                },

                tooltip: {
                    custom: ({ seriesIndex, dataPointIndex, w }) => {
                        const seriesConf = w.config.series[seriesIndex];
                        const meta = seriesConf.meta;
                        const group = groups.find((g) => g.key === meta.groupKey);
                        return `<div class="apexcharts-tooltip-title p-2 fw-bold">${escapeHtml(seriesConf.name)} — ${escapeHtml(months[dataPointIndex].label)}</div>`
                            + buildDeadlineTooltipHtml(group?.data || [], months[dataPointIndex], meta.isOverdue, metric, meta.userIds);
                    }
                },
                stroke: {
                    show: true,
                    colors: ['#fff7f7f2'],
                    width: 1
                },
                grid: {
                    borderColor: '#4a3838'
                }
            };
        };

        const renderDeadlineChart = () => {
            const elementId = 'deadline_performance_chart';
            const el = document.getElementById(elementId);
            if (!el) return;

            chartInstances[elementId] = new ApexCharts(el, buildDeadlineChartOptions());
            chartInstances[elementId].render();
        };

        // Δεν υπάρχει backend endpoint με ώρες ανά subtask, οπότε οι ώρες του task μοιράζονται ισομερώς στα subtasks
        // του — ίδια λογική προσέγγισης με το estimateShare ανά χρήστη παρακάτω.
        const collectTaskAndSubtaskDeadlines = async (project, reportTasks) => {
            const tasksResponse = await fetchAllTasks({ params: { id: project.info.id, per_page: 100, archived: 0 } });
            const taskEntities = tasksResponse?.data?.data?.data || [];

            await Promise.all(taskEntities.map(async (taskEntity) => {
                const taskId = taskEntity.info?.id;
                const reportTask = reportTasks.find((t) => String(t.task_id) === String(taskId));
                const taskHours = reportTask ? parseHoursValue(reportTask.task_total_formatted) : 0;

                const taskContributorHours = {};
                (reportTask?.users || []).forEach((u) => {
                    taskContributorHours[u.userId] = Math.round(parseHoursValue(u.task_worked_hours_formatted) * 10) / 10;
                });

                classifyDeadlineEntity(deadlineDataset.tasks, {
                    endDate: taskEntity.info?.endDate,
                    statusId: taskEntity.status?.id,
                    hours: taskHours,
                    label: `${project.info?.code || ''} — ${taskEntity.info?.title || '-'}`.trim(),
                    contributors: taskContributorHours
                });

                const subtasksResponse = await fetchAllSubtasks({ params: { id: taskId, per_page: 100, archived: 0 } });
                const subtaskEntities = subtasksResponse?.data?.data?.data || [];
                if (!subtaskEntities.length) return;

                // Δεν υπάρχει backend endpoint με ώρες ανά subtask (ούτε ανά χρήστη), οπότε οι ώρες του task
                // (συνολικές και ανά χρήστη) μοιράζονται ισομερώς στα subtasks του — ίδια λογική προσέγγισης με
                // το estimateShare ανά χρήστη παρακάτω.
                const hoursPerSubtask = taskHours / subtaskEntities.length;
                const subtaskContributorHours = {};
                Object.entries(taskContributorHours).forEach(([userId, hours]) => {
                    subtaskContributorHours[userId] = Math.round((hours / subtaskEntities.length) * 10) / 10;
                });

                subtaskEntities.forEach((subtaskEntity) => {
                    classifyDeadlineEntity(deadlineDataset.subtasks, {
                        endDate: subtaskEntity.info?.endDate,
                        statusId: subtaskEntity.status?.id,
                        hours: hoursPerSubtask,
                        label: `${taskEntity.info?.title || '-'} — ${subtaskEntity.info?.title || '-'}`.trim(),
                        contributors: subtaskContributorHours
                    });
                });
            }));
        };

        const fetchKpiData = async () => {
            isLoading.value = true;
            destroyCharts();
            deadlineDataset.projects = [];
            deadlineDataset.tasks = [];
            deadlineDataset.subtasks = [];

            const projectsResponse = await fetchAll({ params: { active: 1, per_page: 100 } });
            const projects = projectsResponse?.data?.data?.data || [];

            const kpiList = [];
            const userTotals = {};

            for (const project of projects) {
                const estimatedTime = parseFloat(project.info?.estimatedTime) || 0;
                if (!estimatedTime) continue; // Χωρίς εκτίμηση δεν υπάρχει KPI να συγκριθεί

                const reportResponse = await getReportData({ projectId: project.info.id });
                const reportData = reportResponse?.data?.data?.data;
                const hoursLogged = parseHoursValue(reportData?.total_project_hours);
                const tasks = reportData?.tasks || [];

                // Ώρες ανά χρήστη (από τα tasks του project) για το personnel breakdown ΚΑΙ για το φιλτράρισμα
                // ανά χρήστη του deadline chart παρακάτω.
                const contributors = {};
                tasks.forEach((task) => {
                    (task.users || []).forEach((u) => {
                        if (!contributors[u.userId]) {
                            contributors[u.userId] = { username: u.username, hours: 0 };
                        }
                        const taskHours = parseHoursValue(u.task_worked_hours_formatted);
                        contributors[u.userId].hours += taskHours;

                        if (!userTotals[u.userId]) {
                            userTotals[u.userId] = { username: u.username, hoursLogged: 0, estimateShare: 0, tasks: [] };
                        }
                        userTotals[u.userId].tasks.push({
                            taskId: task.task_id,
                            title: task.title,
                            projectId: project.info.id,
                            projectCode: project.info.code,
                            hours: taskHours
                        });
                    });
                });

                const projectContributorHours = {};
                Object.entries(contributors).forEach(([userId, c]) => {
                    projectContributorHours[userId] = Math.round(c.hours * 10) / 10;
                });

                classifyDeadlineEntity(deadlineDataset.projects, {
                    endDate: project.info?.endDate,
                    statusId: project.status?.id,
                    hours: hoursLogged,
                    label: project.info?.code || project.info?.title || `#${project.info?.id}`,
                    contributors: projectContributorHours
                });
                await collectTaskAndSubtaskDeadlines(project, tasks);

                const contributorIds = Object.keys(contributors);
                const perUserShare = contributorIds.length ? estimatedTime / contributorIds.length : 0;

                contributorIds.forEach((userId) => {
                    if (!userTotals[userId]) {
                        userTotals[userId] = { username: contributors[userId].username, hoursLogged: 0, estimateShare: 0, tasks: [] };
                    }
                    userTotals[userId].hoursLogged += contributors[userId].hours;
                    userTotals[userId].estimateShare += perUserShare;
                });

                kpiList.push({
                    id: project.info.id,
                    code: project.info.code,
                    client: project.client?.title || '-',
                    manager: project.manager?.username || '-',
                    hoursLogged,
                    estimatedTime,
                    percentage: (hoursLogged / estimatedTime) * 100
                });
            }

            projectsKpi.value = kpiList.sort((a, b) => b.percentage - a.percentage);

            personnelPerformance.value = Object.entries(userTotals)
                .map(([userId, u]) => ({
                    userId,
                    username: u.username,
                    hoursLogged: u.hoursLogged,
                    estimateShare: u.estimateShare,
                    percentage: u.estimateShare ? (u.hoursLogged / u.estimateShare) * 100 : 0,
                    tasks: u.tasks.sort((a, b) => b.hours - a.hours),
                    show: false
                }))
                .sort((a, b) => b.percentage - a.percentage);

            // Βήμα (rank) ανά χρήστη: οι 8 πιο απασχολημένοι παίρνουν δικό τους βήμα στις πράσινη/κόκκινη ράμπα,
            // οι υπόλοιποι μαζεύονται στο "Λοιποί" — χρησιμοποιείται από το deadline chart για το stacking ανά χρήστη
            userRankMap = {};
            [...personnelPerformance.value]
                .sort((a, b) => b.hoursLogged - a.hoursLogged)
                .slice(0, GREEN_SHADES.length)
                .forEach((p, i) => { userRankMap[p.userId] = i; });

            const allDeadlineItems = [...deadlineDataset.projects, ...deadlineDataset.tasks, ...deadlineDataset.subtasks];
            const hasOtherUsers = allDeadlineItems.some((item) => {
                const ids = Object.keys(item.contributors);
                return ids.length && ids.some((uid) => !(uid in userRankMap));
            });
            const hasUnassigned = allDeadlineItems.some((item) => !Object.keys(item.contributors).length);

            deadlineLegend.value = [
                ...Object.entries(userRankMap).map(([userId, rank]) => ({
                    key: userId,
                    label: personnelPerformance.value.find((p) => p.userId === userId)?.username || userId,
                    onTimeColor: GREEN_SHADES[rank],
                    overdueColor: RED_SHADES[rank]
                })),
                ...(hasOtherUsers ? [{ key: '__other__', label: 'Λοιποί χρήστες', onTimeColor: OTHER_GREEN, overdueColor: OTHER_RED }] : []),
                ...(hasUnassigned ? [{ key: '__unassigned__', label: 'Χωρίς ανάθεση', onTimeColor: UNASSIGNED_GREEN, overdueColor: UNASSIGNED_RED }] : [])
            ];

            isLoading.value = false;

            await nextTick();
            renderDeadlineChart();
        };

        const totalHoursLogged = computed(() =>
            projectsKpi.value.reduce((sum, project) => sum + project.hoursLogged, 0)
        );

        const totalEstimatedHours = computed(() =>
            projectsKpi.value.reduce((sum, project) => sum + project.estimatedTime, 0)
        );

        const averageUtilization = computed(() => {
            if (!totalEstimatedHours.value) return 0;
            return (totalHoursLogged.value / totalEstimatedHours.value) * 100;
        });

        const selectedUserName = computed(() =>
            personnelPerformance.value.find((p) => p.userId === selectedUserId.value)?.username || ''
        );

        const toggleUserFilter = (userId) => {
            selectedUserId.value = selectedUserId.value === userId ? null : userId;
        };

        const escapeHtml = (value) =>
            String(value ?? '').replace(/[&<>"']/g, (char) => ({
                '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
            }[char]));

        // PDF export μέσω του native print dialog του browser (ο χρήστης επιλέγει "Αποθήκευση ως PDF"),
        // ώστε τα ελληνικά να αποδίδονται σωστά χωρίς embedding γραμματοσειράς.
        const exportToPdf = () => {
            closeExportMenu();
            window.print();
        };

        const exportToExcel = () => {
            closeExportMenu();

            const personnelRows = personnelPerformance.value.map((p) => `
                <tr>
                    <td>${escapeHtml(p.username)}</td>
                    <td>${formatHours(p.hoursLogged)}</td>
                    <td>${formatHours(p.estimateShare)}</td>
                    <td>${Math.round(p.percentage)}%</td>
                </tr>`).join('');

            const projectRows = projectsKpi.value.map((p) => `
                <tr>
                    <td>${escapeHtml(p.code)}</td>
                    <td>${escapeHtml(p.manager)}</td>
                    <td>${formatHours(p.hoursLogged)}</td>
                    <td>${formatHours(p.estimatedTime)}</td>
                    <td>${Math.round(p.percentage)}%</td>
                </tr>`).join('');

            const html = `
                <html>
                <head><meta charset="UTF-8"></head>
                <body>
                    <h3>Συγκριτική Απόδοση Προσωπικού</h3>
                    <table border="1">
                        <tr><th>Χρήστης</th><th>Ώρες Εργασίας</th><th>Εκτιμώμενο Μερίδιο</th><th>%</th></tr>
                        ${personnelRows}
                    </table>
                    <br/>
                    <h3>Συγκριτική Απόδοση Ομάδων (Projects)</h3>
                    <table border="1">
                        <tr><th>Project</th><th>Manager</th><th>Ώρες Εργασίας</th><th>Εκτιμώμενες Ώρες</th><th>%</th></tr>
                        ${projectRows}
                    </table>
                </body>
                </html>`;

            const blob = new Blob(['﻿' + html], { type: 'application/vnd.ms-excel' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = 'reporting-analytics.xls';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(link.href);
        };

        onMounted(() => {
            fetchKpiData();
        });

        onBeforeUnmount(() => {
            destroyCharts();
        });

        watch([deadlineChartMetric, selectedUserId], async () => {
            if (isLoading.value) return;
            destroyCharts();
            await nextTick();
            renderDeadlineChart();
        });

        return {
            isLoading,
            projectsKpi,
            personnelPerformance,
            personnelViewTab,
            deadlineChartMetric,
            selectedUserId,
            selectedUserName,
            deadlineLegend,
            isExportMenuOpen,
            totalHoursLogged,
            totalEstimatedHours,
            averageUtilization,
            fetchKpiData,
            formatHours,
            statusColor,
            toggleUserFilter,
            toggleExportMenu,
            closeExportMenu,
            exportToPdf,
            exportToExcel
        };
    },
    metaInfo: {
        title: 'Reporting & Analytics Menu',
        meta: [
            { name: 'description', content: 'Reporting & Analytics Menu' },
            { property: 'og:title', content: 'Reporting & Analytics Menu' },
            { property: 'og:description', content: 'KPI dashboards για ώρες και πλήθος εργασιών ανά εκτίμηση project.' }
        ]
    }
};
</script>

<style scoped>
.btn.test:hover{
    color: white;
}
.dropdown-menu.reportingAnalytics{
    right: -5px;
    position: absolute;
}
.kpi-tile {
    background: #e4e9ef;
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.kpi-label {
    font-size: 0.85rem;
    color: #583b3b;
    font-weight: 600;
}

.kpi-value {
    font-size: 1.6rem;
    font-weight: 700;
}

.legend-dot {
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    margin-right: 4px;
}

.bar-track {
    width: 100%;
    height: 8px;
    background: #eaeaea;
    border-radius: 99px;
    overflow: hidden;
}

.bar-fill {
    height: 100%;
    border-radius: 99px;
}

.selected-user-row {
    background: #dde2f1;
}
</style>

<style>
@media print {
    #main-menu,
    #main-header {
        display: none !important;
    }

    #main-container {
        margin: 0 !important;
        padding: 0 !important;
        width: 100% !important;
    }

    .no-print {
        display: none !important;
    }
}
</style>
